import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, View, useWindowDimensions } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { MealType, MealUtils } from '@fytness/shared';
import { formatISO } from 'date-fns';
import { useRouter } from 'expo-router';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';
import { DatePicker } from '@/components/date-picker/DatePicker';
import { useHistoryMinDate } from '~/calendar/hooks/useHistoryMinDate';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { useCurrentLayout } from '~/global/hooks/useCurrentLayout';
import { breakpointPx } from '@/constants/LayoutBreakpoints';
import { db } from '@/lib/local-api';
import { ConsumedDateRange } from '@/lib/local-api/meal/consumed-date-range';
import { MealSlotGrouper } from '@/lib/local-api/meal/meal-slot-grouper';
import {
  GetMealDocument,
  MealsByIdsDocument,
  type Meal,
} from '~/graphql/generated/graphql';
import { useModal } from '@/contexts/ModalContext';
import { MealSelectorModal } from '../meal-selector-modal/MealSelectorModal';
import { MealScannerModal } from '../meal-scanner-modal/MealScannerModal';
import { MealTypeSelector } from '../meal-type-selector/MealTypeSelector';
import { MealActionButtons } from '../meal-action-buttons/MealActionButtons';
import {
  MealLogCard,
  type MealLogCardMeal,
} from '../meal-log-card/MealLogCard';
import { MealLogSummary } from '../meal-log-summary/MealLogSummary';
import { MealLogEmpty } from '../meal-log-empty/MealLogEmpty';
import { MealLogSelectionBar } from '../meal-log-selection-bar/MealLogSelectionBar';
import { useMealLogCopy } from '../meal-log-copy/useMealLogCopy';
import { StringUtils } from '~/global/utils/string.utils';
import type { AdjustedNutrition } from '@fytness/shared';

type MealLog = {
  id: string;
  niceId: string | null;
  mealId: string;
  mealType: string | null;
  consumedAmount: number | null;
  consumedAmountUnit: string | null;
  consumedAt: string;
};

export interface MealLogScreenProps {
  date: Date;
  selectedType: MealType;
  onTypeChange: (type: MealType) => void;
}

export const MealLogScreen = ({
  date,
  selectedType,
  onTypeChange,
}: MealLogScreenProps) => {
  const { t } = useTranslation('meal');
  const router = useRouter();
  const minimumDate = useHistoryMinDate();
  const powerSync = usePowerSync();
  const { showModal } = useModal();
  const layout = useCurrentLayout();
  const isDesktop = layout === 'sidebar';
  const { width } = useWindowDimensions();

  // The bare local calendar date this screen WRITES as `consumedAt`.
  const dateStr = formatISO(date, { representation: 'date' });

  // The SQL bounds are a deliberate superset (widened a day each side); the
  // exact local-day decision is made in memory by `filterToDays`.
  const { fromDate, toDate } = ConsumedDateRange.forSingleDay(date);

  const { data: rawMealLogs = [] } = useLivePowerSyncQuery<MealLog>(
    db.MealLog.queries.findByDateRange(fromDate, toDate),
    [fromDate, toDate],
  );

  const allMealLogs = useMemo(
    () => ConsumedDateRange.filterToDays(rawMealLogs, date, date),
    [rawMealLogs, date],
  );

  const filteredLogs = useMemo(
    () => MealSlotGrouper.inSlot(allMealLogs, selectedType),
    [allMealLogs, selectedType],
  );

  // Derived from the WHOLE day, not the visible slot: a selection is day-scoped
  // (DD1), so the summary must be able to name items picked in a slot the user
  // has navigated away from. The query is already batched, so this costs nothing.
  const mealIds = useMemo(
    () => Array.from(new Set(allMealLogs.map((l) => l.mealId).filter(Boolean))),
    [allMealLogs],
  );

  const { data: mealsData, previousData } = useQuery(MealsByIdsDocument, {
    variables: { ids: mealIds as string[] },
    skip: mealIds.length === 0,
  });

  // When mealIds change (a meal is added/removed) Apollo refetches with new
  // variables and `mealsData` is briefly undefined (cache miss). Fall back to
  // `previousData` so already-fetched cards stay rendered instead of the whole
  // list blanking out and snapping back.
  const mealsById = useMemo(() => {
    const map = new Map<string, Meal>();
    ((mealsData ?? previousData)?.mealsByIds ?? []).forEach((m) =>
      map.set(m.id, m as Meal),
    );
    return map;
  }, [mealsData, previousData]);

  const totals = useMemo<AdjustedNutrition>(() => {
    return filteredLogs.reduce(
      (acc, log) => {
        const meal = mealsById.get(log.mealId);
        if (!meal) return acc;
        const adj = MealUtils.adjustedNutrition(meal, log.consumedAmount ?? 0);
        return {
          calories: (acc.calories ?? 0) + (adj.calories ?? 0),
          protein: (acc.protein ?? 0) + (adj.protein ?? 0),
          carbs: (acc.carbs ?? 0) + (adj.carbs ?? 0),
          fats: (acc.fats ?? 0) + (adj.fats ?? 0),
          fiber: (acc.fiber ?? 0) + (adj.fiber ?? 0),
          water: (acc.water ?? 0) + (adj.water ?? 0),
        };
      },
      { calories: 0, protein: 0, carbs: 0, fats: 0, fiber: 0, water: 0 },
    );
  }, [filteredLogs, mealsById]);

  const createMutation = useMutation({
    mutationFn: (input: {
      mealId: string;
      consumedAmount: number;
      consumedAt: string;
      mealType: string;
    }) => db.MealLog.mutations.create(powerSync, input),
  });

  const updateMutation = useMutation({
    mutationFn: (input: {
      id: string;
      consumedAmount: number;
      consumedAmountUnit: string | null;
    }) =>
      db.MealLog.mutations.update(powerSync, input.id, {
        consumedAmount: input.consumedAmount,
        consumedAmountUnit: input.consumedAmountUnit,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => db.MealLog.mutations.delete(powerSync, id),
  });

  const [getMeal] = useLazyQuery(GetMealDocument);

  const handleMealSelected = useCallback(
    async (meal: Meal) => {
      const result = await getMeal({ variables: { id: meal.id } });
      const fullMeal = result.data?.meal as Meal | undefined;
      if (!fullMeal) return;

      const servingSize = fullMeal.servingSize
        ? (StringUtils.parseFloat(fullMeal.servingSize.toString()) ?? 1)
        : 1;

      createMutation.mutate({
        mealId: meal.id,
        consumedAmount: servingSize,
        consumedAt: dateStr,
        mealType: selectedType,
      });
    },
    [getMeal, dateStr, selectedType, createMutation],
  );

  const handleSearch = useCallback(() => {
    showModal(MealSelectorModal, {
      onMealSelected: handleMealSelected,
    });
  }, [showModal, handleMealSelected]);

  const handleScan = useCallback(() => {
    showModal(MealScannerModal, {
      onBarcodeScanned: (barcode: string) => {
        showModal(MealSelectorModal, {
          barcode,
          hideSearch: true,
          onMealSelected: handleMealSelected,
        });
      },
    });
  }, [showModal, handleMealSelected]);

  const handleRemove = useCallback(
    (mealLogId: string) => {
      deleteMutation.mutate(mealLogId);
    },
    [deleteMutation],
  );

  const handleAmountChange = useCallback(
    (mealLogId: string, amount: number, unit: string | null) => {
      updateMutation.mutate({
        id: mealLogId,
        consumedAmount: amount,
        consumedAmountUnit: unit,
      });
    },
    [updateMutation],
  );

  const hasMeals = filteredLogs.length > 0;

  // Selection state lives ABOVE the slot filter and is keyed to the DAY: the
  // user can tick two items in Breakfast, switch to Lunch, tick a third and
  // copy all three in one operation → one summary → one undo (DD1). It is
  // therefore cleared on a day change or on leaving selection mode, and
  // deliberately NOT on a slot change.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, [dateStr]);

  const { dispatchMealLogCopy } = useMealLogCopy();

  const mealNamesById = useMemo(() => {
    const names: Record<string, string> = {};
    mealsById.forEach((meal, id) => {
      if (meal.name) names[id] = meal.name;
    });
    return names;
  }, [mealsById]);

  // Sources are resolved from the day's `allMealLogs`, never from `filteredLogs`
  // — otherwise a selection made in another slot would silently be dropped.
  const selectedLogs = useMemo(
    () => allMealLogs.filter((log) => selectedIds.has(log.id)),
    [allMealLogs, selectedIds],
  );

  const perSlotCounts = useMemo(() => {
    const counts: Partial<Record<MealType, number>> = {};
    selectedLogs.forEach((log) => {
      const slot = MealSlotGrouper.slotOf(log) as MealType;
      counts[slot] = (counts[slot] ?? 0) + 1;
    });
    return counts;
  }, [selectedLogs]);

  const exitSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const handleToggleSelectMode = useCallback(() => {
    setSelectionMode((active) => {
      if (active) setSelectedIds(new Set());
      return !active;
    });
  }, []);

  const handleToggleSelect = useCallback((mealLogId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(mealLogId)) {
        next.delete(mealLogId);
      } else {
        next.add(mealLogId);
      }
      return next;
    });
  }, []);

  // "Select all" is scoped to what the user can actually see, and ADDS to the
  // cross-slot selection rather than replacing it.
  const handleSelectAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredLogs.forEach((log) => next.add(log.id));
      return next;
    });
  }, [filteredLogs]);

  const dispatchForSelection = useCallback(
    (mode: 'copy' | 'move') => {
      if (selectedLogs.length === 0) return;
      dispatchMealLogCopy({
        sourceIds: selectedLogs.map((log) => log.id),
        mode,
        defaultDate: date,
        mealNamesById,
      });
      exitSelection();
    },
    [selectedLogs, dispatchMealLogCopy, date, mealNamesById, exitSelection],
  );

  const dispatchForSingle = useCallback(
    (mode: 'copy' | 'move') => (mealLogId: string) => {
      dispatchMealLogCopy({
        sourceIds: [mealLogId],
        mode,
        defaultDate: date,
        mealNamesById,
      });
    },
    [dispatchMealLogCopy, date, mealNamesById],
  );

  const handleCopySingle = useMemo(
    () => dispatchForSingle('copy'),
    [dispatchForSingle],
  );
  const handleMoveSingle = useMemo(
    () => dispatchForSingle('move'),
    [dispatchForSingle],
  );

  const selectionBar = selectionMode ? (
    <MealLogSelectionBar
      totalCount={selectedIds.size}
      perSlotCounts={perSlotCounts}
      onSelectAllVisible={handleSelectAllVisible}
      onCopy={() => dispatchForSelection('copy')}
      onMove={() => dispatchForSelection('move')}
      onCancel={exitSelection}
    />
  ) : null;

  const renderCard = (log: MealLog) => {
    const meal = mealsById.get(log.mealId);
    if (!meal) return null;
    return (
      <MealLogCard
        key={log.id}
        mealLogId={log.id}
        meal={meal as MealLogCardMeal}
        consumedAmount={
          StringUtils.parseFloat(log.consumedAmount?.toString() ?? '1') ?? 1
        }
        consumedAmountUnit={log.consumedAmountUnit}
        onRemove={handleRemove}
        onAmountChange={handleAmountChange}
        onCopy={handleCopySingle}
        onMove={handleMoveSingle}
        selectable={selectionMode}
        selected={selectedIds.has(log.id)}
        onToggleSelect={handleToggleSelect}
      />
    );
  };

  if (isDesktop) {
    // Lay cards out in a grid: 3 columns, 4 on 2xl+. NativeWind grid is unreliable on
    // RN/RN-web and this branch can render on a wide native tablet, so chunk into rows
    // of equal-width (flex-1) cells, padding the last row to keep card widths.
    const cols = width >= breakpointPx['2xl'] ? 4 : 3;
    const cells = [
      ...filteredLogs.map(renderCard),
      <AddMealCardDesktop
        key="add"
        onSearch={handleSearch}
        onScan={handleScan}
      />,
    ].filter(Boolean);
    const rows: (typeof cells)[] = [];
    for (let i = 0; i < cells.length; i += cols) {
      rows.push(cells.slice(i, i + cols));
    }

    return (
      <View className="flex-1 flex flex-col">
        {/* Cards area — grid of equal-width columns */}
        {hasMeals ? (
          <ScrollView className="flex-1" contentContainerClassName="p-8">
            <View testID="meal-log-list" className="flex flex-col gap-6">
              {rows.map((row, rowIndex) => (
                <View
                  key={rowIndex}
                  className="flex flex-row gap-6 items-stretch"
                >
                  {row.map((cell, cellIndex) => (
                    <View key={cellIndex} className="flex-1">
                      {cell}
                    </View>
                  ))}
                  {Array.from({ length: cols - row.length }).map((_, i) => (
                    <View key={`spacer-${i}`} className="flex-1" />
                  ))}
                </View>
              ))}
            </View>
          </ScrollView>
        ) : (
          <View className="flex-1 items-center justify-center">
            <MealLogEmpty />
          </View>
        )}

        {selectionBar ? (
          <View className="items-center px-8 pb-4">
            <View style={{ width: 560, maxWidth: width - 64 }}>
              {selectionBar}
            </View>
          </View>
        ) : null}

        {/* Bottom bar */}
        <View className="flex flex-row items-center gap-8 px-8 py-5 border-t border-border">
          <View style={{ width: 320 }}>
            <MealTypeSelector selected={selectedType} onSelect={onTypeChange} />
          </View>
          <MealActionButtons
            onSearch={handleSearch}
            onScan={handleScan}
            onToggleSelectMode={handleToggleSelectMode}
            selectModeActive={selectionMode}
            hasLogs={hasMeals}
          />
          <View className="flex-1" />
          {hasMeals && <DesktopSummaryInline totals={totals} />}
        </View>
      </View>
    );
  }

  // Mobile layout — vertical stack
  return (
    <ScrollView className="flex-1" contentContainerClassName="p-6 gap-6 pb-24">
      <DatePicker
        variant="card"
        value={date}
        minimumDate={minimumDate ?? undefined}
        onChange={(d) =>
          router.setParams({ date: formatISO(d, { representation: 'date' }) })
        }
      />
      <MealTypeSelector selected={selectedType} onSelect={onTypeChange} />
      <MealActionButtons
        onSearch={handleSearch}
        onScan={handleScan}
        onToggleSelectMode={handleToggleSelectMode}
        selectModeActive={selectionMode}
        hasLogs={hasMeals}
      />

      {hasMeals ? (
        <View testID="meal-log-list" className="gap-6">
          {filteredLogs.map(renderCard)}
          <MealLogSummary totals={totals} />
        </View>
      ) : (
        <MealLogEmpty />
      )}

      {selectionBar}
    </ScrollView>
  );
};

const AddMealCardDesktop = ({
  onSearch,
  onScan,
}: {
  onSearch: () => void;
  onScan: () => void;
}) => {
  const { t } = useTranslation('meal');

  return (
    <View className="flex-1 flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-border py-8 px-5">
      <Icon name="Plus" size={24} className="text-text-muted" />
      <Text className="text-sm font-inter-medium text-text-muted">
        {t('addAnotherItem')}
      </Text>
      <View className="flex flex-row gap-3">
        <Pressable
          onPress={onSearch}
          className="flex flex-row items-center gap-2 rounded-[10px] bg-surface-elevated px-4 py-2.5 active:opacity-70"
        >
          <Icon name="Search" size={16} className="text-text-muted" />
          <Text className="text-[13px] font-inter-medium text-text-muted">
            {t('search')}
          </Text>
        </Pressable>
        <Pressable
          onPress={onScan}
          className="flex flex-row items-center gap-2 rounded-[10px] bg-surface-elevated px-4 py-2.5 active:opacity-70"
        >
          <Icon name="ScanBarcode" size={16} className="text-text-muted" />
          <Text className="text-[13px] font-inter-medium text-text-muted">
            {t('scan')}
          </Text>
        </Pressable>
      </View>
    </View>
  );
};

const DesktopSummaryInline = ({ totals }: { totals: AdjustedNutrition }) => {
  const { t } = useTranslation('meal');

  const macros = [
    { label: t('nutrition.carbs.longLabel'), value: totals.carbs },
    { label: t('nutrition.protein.longLabel'), value: totals.protein },
    { label: t('nutrition.fats.longLabel'), value: totals.fats },
    { label: t('nutrition.fiber.longLabel'), value: totals.fiber },
  ];

  return (
    <View className="flex flex-row items-center gap-6 rounded-xl bg-surface py-3 px-5">
      {macros.map(({ label, value }) => (
        <View key={label} className="flex flex-col items-center gap-0.5">
          <Text className="text-sm font-sora-semibold text-text-primary">
            {value != null ? `${Math.round(value * 10) / 10}g` : '0g'}
          </Text>
          <Text className="text-[11px] text-text-muted">{label}</Text>
        </View>
      ))}
      <Text className="text-sm font-sora-semibold text-accent">
        {totals.calories != null
          ? `${Math.round(totals.calories)} ${t('nutrition.kcal.unit')}`
          : `0 ${t('nutrition.kcal.unit')}`}
      </Text>
    </View>
  );
};
